import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { WithTranslation, withTranslation } from 'react-i18next';
import { IReportType } from 'helpers/interfaces';
import { Layout, Menu } from 'antd';
import {
    canAccessReports,
    getUserAuthorities,
    localStorageHelper,
    mediaQueryHooksHOC,
    projectDetection,
} from 'utils';
import { Map } from 'helpers/types/Map';
import * as i18next from 'i18next';
import logo from 'images/logo.png';
import koalaLogo from 'images/KOALA-logo.png';
import type { MenuProps } from 'antd';

import {
    DollarOutlined,
    FileTextOutlined,
    PlayCircleOutlined,
    ShopOutlined,
    TeamOutlined,
    DisconnectOutlined,
    CrownOutlined,
    CheckSquareOutlined,
    FieldTimeOutlined,
    FileSearchOutlined,
    GiftOutlined,
    ApiOutlined,
    WalletOutlined,
} from '@ant-design/icons';
import { IUserState } from 'redux/reducers/user/user-reducer';
import { userState } from 'redux/selectors/user-selectors';
import { withRouter } from '../../helpers/HOCs/withRouter';
import { IS_KOALA } from '../../utils/config';

const { Sider } = Layout;
const MENU_WIDTH_FOR_TABLET = 180;
const MENU_WIDTH_FOR_DESKTOP = 256;

interface IProps {
    t: i18next.TFunction;
    logout: Function;
    userState: IUserState;
    mediaQuery: any;
    location: Location;
}

type MenuItemType = {
    key: string;
    icon?: React.ReactNode;
    label: React.ReactNode;
    children?: MenuItemType[];
    disabled?: boolean;
    tooltip?: string;
};

const configProviderMainMenuItems = [
    {
        description: 'operators',
        link: `/config-provider/operators`,
        component: <ShopOutlined />,
        authority: 'CONFIGURE_INTEGRATION',
    },
    {
        description: 'games',
        link: `/config-provider/games`,
        component: <PlayCircleOutlined />,
        authority: 'CONFIGURE_INTEGRATION',
    },
    {
        description: 'currencies',
        link: `/config-provider/currencies`,
        component: <DollarOutlined />,
        authority: 'CONFIGURE_INTEGRATION',
    },
    {
        description: 'resolve_sessions',
        link: `/config-provider/broken-rounds`,
        component: <DisconnectOutlined />,
        authority: 'SESSION_MANAGEMENT',
    },
    {
        description: 'cash_rewards',
        link: `/config-provider/cash-rewards`,
        component: <WalletOutlined />,
        authority: 'SESSION_MANAGEMENT',
    },
    {
        description: 'unfinished_rounds',
        link: `/config-provider/unfinished-rounds`,
        component: <FieldTimeOutlined />,
        authority: 'CONFIGURE_INTEGRATION',
    },
    {
        description: 'round_info',
        link: `/config-provider/round-info`,
        component: <FileSearchOutlined />,
        authority: 'TECHNICAL_REPORT',
    },
    {
        description: 'critical_checksum',
        link: `/casino/reports/critical-checksum`,
        component: <CheckSquareOutlined />,
        authority: 'CRITICAL_FILES_CHECK_HISTORY',
    },
    {
        description: 'api_access',
        link: `/config-provider/api-access`,
        component: <ApiOutlined />,
        authority: 'CONFIGURE_INTEGRATION',
    },
];

const getConfigProviderMenuItems = (): MenuItemType[] => {
    const authorities = getUserAuthorities();
    return configProviderMainMenuItems
        .filter(({ authority }) => authorities.includes(authority))
        .map(({ description, link, component }) => ({
            key: link,
            icon: component,
            label: description,
        }));
};

const convertToComponents = (t: i18next.TFunction) => {
    const authorities = getUserAuthorities();
    const convertingMap: Map<IReportType> = {
        REPORT_PLAYER_GROUP_REVENUE: {
            description: 'casino_performance',
            link: `/casino/reports/casino-performance`,
            id: 'casino_performance_reports_item',
        },
        REPORT_PLAYER_REVENUE: {
            description: 'players_performance',
            link: `/casino/reports/players-performance`,
            id: 'players_performance_reports_item',
        },
        REPORT_GAME_REVENUE: {
            description: 'games_performance',
            link: `/casino/reports/games-performance`,
            id: 'games_performance_reports_item',
        },
        CRITICAL_FILES_CHECK_HISTORY: {
            description: 'critical_checksum',
            link: `/casino/reports/critical-checksum`,
            id: 'critical_checksum_reports_item',
        },
        REPORT_MONTHLY_BILLING: {
            description: 'monthly_billing_report',
            link: `/casino/reports/monthly-billing-report`,
            id: 'monthly_billing_reports_item',
        },
    };
    const result: MenuItemType[] = [];
    for (const reportType in authorities) {
        if (
            Object.prototype.hasOwnProperty.call(authorities, reportType) &&
            authorities[reportType]
        ) {
            const convertedItem = convertingMap[authorities[reportType]];
            if (convertedItem) {
                const { description, link } = convertedItem;
                result.push({
                    key: link,
                    label: t(description),
                });
            }
        }
    }
    return result;
};

type MenuItem = Required<MenuProps>['items'][number];

function getItem(
    label: React.ReactNode,
    key: React.Key,
    icon?: React.ReactNode,
    children?: MenuItem[],
): MenuItem {
    return {
        key,
        icon,
        children,
        label,
    } as MenuItem;
}

const MainMenu: React.FC<IProps & WithTranslation> = ({ t, userState, mediaQuery, location }) => {
    const [minified, setMinified] = React.useState(false);
    const navigate = useNavigate();
    const authorities = getUserAuthorities();
    const shouldDisplayPromotions =
        authorities.includes('MANAGE_CAMPAIGNS') ||
        authorities.includes('MANAGE_SUPPLIERS_CAMPAIGNS') ||
        authorities.includes('VIEW_CAMPAIGNS');
    const shouldDisplayFreeRounds =
        localStorageHelper.getChainedValue('user.brandFeatures.directFreeRoundsSupporting') &&
        localStorageHelper.getChainedValue('user.brandFeatures.supportsFreeRounds') &&
        shouldDisplayPromotions;
    const shouldDisplayAwardedFeature =
        localStorageHelper.getChainedValue('user.brandFeatures.supportsAwardedFeature') &&
        shouldDisplayPromotions;
    const { pathname } = location;
    const { isBO, isCP } = projectDetection;
    const isAdmin =
        (userState && userState.role === 'ADMIN') ||
        localStorageHelper.getChainedValue('user.role') === 'ADMIN';
    const { isTablet } = mediaQuery;
    const reportsChildren = convertToComponents(t).map((child) => getItem(child.label, child.key));
    const menuItems: MenuItem[] = [];
    if (isBO() && canAccessReports()) {
        menuItems.push(
            getItem(
                t('main_menu_reports'),
                '/casino/reports',
                <FileTextOutlined />,
                reportsChildren,
            ),
        );
    }
    if (isCP()) {
        menuItems.push(
            ...getConfigProviderMenuItems().map((item) =>
                getItem(t(item.label as string), item.key, item.icon),
            ),
        );
    }
    if (isAdmin) {
        menuItems.push(getItem(t('users'), '/casino/users', <TeamOutlined />));
    }
    if (shouldDisplayFreeRounds) {
        menuItems.push(getItem('Free Rounds', '/casino/free-rounds-promotions', <CrownOutlined />));
    }
    if (shouldDisplayAwardedFeature) {
        menuItems.push(getItem('Awarded Features', '/casino/awarded-promotions', <GiftOutlined />));
    }

    const [openKeys, setOpenKeys] = React.useState<string[]>(['/casino/reports']);

    const siderLogo = IS_KOALA ? (
        <img
            className={`transition-all duration-300 ${minified ? 'w-8 h-8' : 'w-16 h-16'}`}
            src={koalaLogo}
            alt="logo"
        />
    ) : (
        <img
            className={`transition-all duration-300 ${minified ? 'w-8' : 'w-32'}`}
            src={logo}
            alt="logo"
        />
    );

    return (
        <Sider
            className="flex flex-col max-h-screen overflow-hidden bg-white border-r border-gray-200"
            theme="light"
            collapsible
            collapsed={minified}
            onCollapse={setMinified}
            width={isTablet ? MENU_WIDTH_FOR_TABLET : MENU_WIDTH_FOR_DESKTOP}
        >
            <div className="flex flex-col h-full">
                <div className="pt-8 pb-6 px-6">
                    <h2 className="text-2xl font-bold text-red-600 tracking-tight">
                        {minified ? 'BO' : 'Back Office'}
                    </h2>
                </div>
                <Menu
                    mode="inline"
                    theme="light"
                    items={menuItems}
                    selectedKeys={[pathname]}
                    openKeys={openKeys}
                    onOpenChange={setOpenKeys}
                    style={{ border: 'none', background: 'transparent' }}
                    onClick={({ key }) => navigate(key as string)}
                />
                <div className="mt-auto flex flex-col items-center pb-6">{siderLogo}</div>
            </div>
        </Sider>
    );
};

const mapStateToProps = (state: any) => ({
    userState: userState(state),
});

export default connect(
    mapStateToProps,
    null,
)(withTranslation()(mediaQueryHooksHOC(withRouter(MainMenu))));
